import { Component, computed, inject, Injector, OnDestroy, Signal, signal, viewChild } from '@angular/core';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { AttrPath, Interventielaag, Table } from '@cumlaude/metadata';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { BasicFilterExpression, CompoundFilterExpression, DataService, ExportDataOptions, ExportFilter } from '../../services/data.service';
import { Attributes, createSelection, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { Level, pathTo, unnest } from '../../services/data-tree';
import { MultiAggregator, noAgg0, SingleAggregator, xAgg0 } from '../../services/aggregation';
import { AggregatorConfig } from '../../shared/dashboard/base-dashboard/aggregator-config';
import { createAggFunctions } from '../../shared/dashboard/base-dashboard/base-dashboard.component';
import { tabulate } from '../../services/tabulate';
import { isUndefined, sortBy, union } from 'lodash-es';
import { sortLike } from '../../services/labels';
import { att } from '../../services/measures';
import { DisplayService } from '../../services/display.service';
import { DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AD_HOC_SELECTION_ID } from '../../services/leerling-selectie.service';
import { Dashboard } from '../../shared/dashboard/base-dashboard/dashboard';
import { combineLatest, Observable, Subject } from 'rxjs';
import { AanwezigheidspiramideComponent } from './aanwezigheidspiramide/aanwezigheidspiramide.component';
import { AanwezigheidspiramidePerMaandComponent } from './aanwezigheidspiramide-per-maand/aanwezigheidspiramide-per-maand.component';
import { OverzichtInterventielagenComponent } from './overzicht-interventielagen/overzicht-interventielagen.component';
import { CustomExportItem, ExportOptions, ExportType } from '../../services/export.service';
import { LoadingType } from '../../services/loading.service';
import { QueryParamStateService } from '../../services/query-param-state.service';
import { AanwezigheidUitsplitsing } from '../../services/weergave-opties';
import { LegendaComponent } from '../../legenda/legenda.component';
import { delayUntilNextRender } from '@cumlaude/shared-utils';
import { getAfgeslotenMaandenFilter } from '../../services/schoolaanwezigheid';
import { FilterName } from '../../services/filter-name';

interface SchoolaanwezigheidI extends Attributes {
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_aanwezig: number;
	aw_m_nr_leerlingen: number;
	xa: any;
}

export interface SchoolaanwezigheidA extends Attributes {
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_aanwezig: number;
	aw_m_nr_leerlingen: number;
	aw_m_nr_leerlingen_x: number;
}

export type SchoolaanwezigheidRequestContext = DataRequestContext<SchoolaanwezigheidA> & {
	columnRoot?: Level<SchoolaanwezigheidA, number[]>;
	groups: AttrPath[];
	subgroups: AttrPath[];
};

@Component({
	selector: 'app-aanwezigheid-overzicht',
	templateUrl: './aanwezigheid-overzicht.component.html',
	styleUrls: ['./aanwezigheid-overzicht.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterBarComponent,
		FilterPanelComponent,
		AanwezigheidspiramideComponent,
		AanwezigheidspiramidePerMaandComponent,
		OverzichtInterventielagenComponent,
		LegendaComponent,
	],
})
export class AanwezigheidOverzichtComponent extends Dashboard implements AggregatorConfig<SchoolaanwezigheidI, SchoolaanwezigheidA>, OnDestroy {
	private readonly dataService = inject(DataService);
	protected readonly displayService = inject(DisplayService);
	protected readonly qp = inject(QueryParamStateService);
	private readonly injector = inject(Injector);

	readonly factTable = Table.fac_aw_aanwezigheid;

	readonly defaultFilters: FilterName[] = [
		'aw_nm_schooljaar',
		'aw_fk_lb.lb_co_brin',
		'aw_fk_vs.vs_nm_vestiging',
		'aw_fk_ilt.ilt_nm_niveau',
		'aw_nr_leerjaar',
		'aw_nm_klas',
		'aw_fun_nm_abs_reden',
		'aw_fun_nm_vak',
		'aw_d_datum.per_d_datum',
	];

	readonly fixedGroups: AttrPath[] = [['aw_nr_maand']];
	readonly fixedSubgroups: AttrPath[] = [['aw_fun_nm_interventielaag']];

	readonly permanentFilters = computed(() => {
		const vestigingen = this.userService.schoolaanwezigheidVestigingen();
		return [
			new BasicFilterExpression(['aw_is_presentieregistratie_verwerkt'], 1),
			getAfgeslotenMaandenFilter(),
			new BasicFilterExpression(['aw_id_vestiging'], vestigingen, 'in'),
		];
	});

	private readonly aanwezigheiduitsplitsing = this.qp.signal_aanwezigheiduitsplitsing();

	private getAanwezigheiduitsplitsingOmschrijving() {
		switch (this.aanwezigheiduitsplitsing()) {
			case AanwezigheidUitsplitsing.LEERLING:
				return 'Aanwezigheid per leerling';
			case AanwezigheidUitsplitsing.KLAS:
				return 'Aanwezigheid per klas';
			case AanwezigheidUitsplitsing.VAK:
				return 'Aanwezigheid per vak';
		}
	}

	protected readonly customExportItems = computed<CustomExportItem[]>(() => {
		const total = this.totalUitsplitsing();
		const uitsplitsing = this.getAanwezigheiduitsplitsingOmschrijving() + (total !== undefined ? ` (${total})` : '');
		return [
			{ label: 'Cumulatieve aanwezigheid', condition: this.exportCumulatieveAanwezigheid, component: this.aanwezigheidspiramide },
			{ label: 'Aanwezigheid per maand', condition: this.exportAanwezigheidPerMaand, component: this.aanwezigheidspiramidePerMaand },
			{
				label: uitsplitsing,
				condition: this.exportUitsplitsing,
				component: this.overzichtInterventielagen,
				disabled: (options) => this.exportUitsplitsingDisabled(options),
			},
		];
	});

	private exportUitsplitsingDisabled(options: ExportOptions): false | string {
		if (options.type !== ExportType.AFBEELDING) return false;

		// Maximaal aantal records voor uitsplitsing bij export naar PNG.
		// Is gebaseerd op 32767 pixels hoogte limiet van canvas, gedeeld door 56 pixels per record, plus ruimte voor headers en aanwezigheid-grafieken
		const MAX_UITSPLITSING = 500;

		const totalUitsplitsing = this.totalUitsplitsing();
		if (totalUitsplitsing === undefined) {
			return 'Bezig met berekenen...';
		} else if (totalUitsplitsing > MAX_UITSPLITSING) {
			return 'De leerlingselectie is te groot om als afbeelding te exporteren. Verklein de selectie met filters of kies een ander exportformaat.';
		}
		return false;
	}

	protected readonly exportCumulatieveAanwezigheid = signal<boolean>(true);
	protected readonly exportAanwezigheidPerMaand = signal<boolean>(true);
	protected readonly exportUitsplitsing = signal<boolean>(false);

	readonly totalUitsplitsing = signal<number | undefined>(undefined);
	private readonly totalUitsplitsingForExport$ = new Subject<number | undefined>();
	readonly exportMode = signal(false);

	readonly requestContext: Signal<SchoolaanwezigheidRequestContext> = toSignal(
		toObservable(this.filterExpressions).pipe(
			switchMap((filters) => {
				const f = new CompoundFilterExpression([...filters, ...this.permanentFilters()]);
				return this.dataService
					.getSchoolAanwezigheidData({
						g: [...this.fixedGroups, ...this.fixedSubgroups],
						r: [0, 1],
						f,
					})
					.pipe(
						map((resp) => {
							const { aggInit, aggCombine } = createAggFunctions(this, ['aw_fun_nm_interventielaag'], resp.measures);
							const records = unnest<SchoolaanwezigheidA, number[]>(resp.data, undefined, 1, aggInit, aggCombine);
							const dataRoot = records[0]?.[0] as Level<SchoolaanwezigheidA, number[]> | undefined;
							const columnRoot = dataRoot === undefined ? undefined : tabulate(dataRoot, sortInterventielaag, undefined, aggCombine);
							return {
								dataRoot,
								columnRoot,
								groupNames: this.fixedGroups.map((p) => p.join('.')),
								subgroupNames: this.fixedSubgroups.map((p) => p.join('.')),
								measureNames: resp.measures,
								filter: f,
								groups: this.fixedGroups,
								subgroups: this.fixedSubgroups,
							};
						})
					);
			})
		),
		{
			initialValue: {
				dataRoot: undefined,
				groupNames: [],
				subgroupNames: [],
				columnRoot: undefined,
				measureNames: [],
				filter: new CompoundFilterExpression([]),
				groups: [],
				subgroups: [],
			},
		}
	);

	readonly dataRootSelection = computed(() => {
		const context = this.requestContext();
		if (isUndefined(context.dataRoot)) return undefined;

		if (this.userService.isBestuur()) return undefined;

		return createSelection(context, pathTo(context.dataRoot), this.getSelectionConfig(context));
	});

	readonly aanwezigheidspiramide = viewChild.required(AanwezigheidspiramideComponent);

	readonly aanwezigheidspiramidePerMaand = viewChild.required(AanwezigheidspiramidePerMaandComponent);

	readonly overzichtInterventielagen = viewChild.required(OverzichtInterventielagenComponent);

	protected singleAggregators: Partial<{ [ai in keyof SchoolaanwezigheidA]: SingleAggregator<SchoolaanwezigheidI, SchoolaanwezigheidA[ai]> }> = {
		aw_m_nr_minuten: noAgg0('aw_m_nr_minuten'),
		aw_m_nr_minuten_aanwezig: noAgg0('aw_m_nr_minuten_aanwezig'),
		aw_m_nr_leerlingen: noAgg0('aw_m_nr_leerlingen'),
		aw_m_nr_leerlingen_x: xAgg0<'aw_m_nr_leerlingen', SchoolaanwezigheidI>('aw_m_nr_leerlingen'),
	};

	protected multiAggregators: MultiAggregator<keyof SchoolaanwezigheidA, SchoolaanwezigheidI, SchoolaanwezigheidA, number>[] = [];

	getSingleAggregators() {
		return this.singleAggregators;
	}
	getMultiAggregators() {
		return this.multiAggregators;
	}

	getSelectionConfig(_context: DataRequestContext<SchoolaanwezigheidA>): SelectionConfig<SchoolaanwezigheidA> {
		const x_leerlingselectie = JSON.stringify([{ id: AD_HOC_SELECTION_ID, naam: '' }]);
		return {
			detailUrl: `/aanwezigheid/statistieken?x_leerlingselectie=${x_leerlingselectie}`,
			mergeQueryParams: true,
			provider: 'schoolaanwezigheid',
			getSize: att('aw_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	override getExportData(options: ExportDataOptions): Observable<Blob> {
		return this.dataService.getAanwezigheidExportData(options);
	}

	override exportAsTable(exportOptions: ExportOptions, _title: string, filters: ExportFilter[]): Observable<Blob> {
		this.loadingService.start(LoadingType.EXPORT, true);

		const exportPages = this.customExportItems()
			.filter(({ condition }) => condition())
			.map(({ component }) => component().exportAsTable(exportOptions, filters));
		return combineLatest(exportPages).pipe(
			map((pages) => {
				return {
					showDatum: exportOptions.showDatum,
					pages: pages,
				};
			}),
			switchMap((tableExport) => this.dataService.getTableExport(tableExport))
		);
	}

	override exportToPng(options: ExportDataOptions): Observable<Blob> {
		return this.exportWithUitsplitsing(() => super.exportToPng(options));
	}

	override exportToPdf(options: ExportDataOptions): Observable<Blob> {
		return this.exportWithUitsplitsing(() => super.exportToPdf(options));
	}

	/**
	 * Handler voor wanneer het totaal van de uitsplitsing wordt bijgewerkt.
	 * Update zowel het signal (voor display) als emit naar de Subject (voor export timing).
	 */
	onTotalChanged(total: number | undefined): void {
		this.totalUitsplitsing.set(total);
		this.totalUitsplitsingForExport$.next(total);
	}

	override ngOnDestroy(): void {
		this.totalUitsplitsingForExport$.complete();
		super.ngOnDestroy();
	}

	private exportWithUitsplitsing(superMethod: () => Observable<Blob>): Observable<Blob> {
		// De uitsplitsing per leerling/klas/vak is een incremental list die veel data kan bevatten.
		// Wanneer deze export-optie is gekozen, moet de data eerst opgehaald en gerenderd worden voordat deze geëxporteerd kan worden.

		// Zonder uitsplitsing (de default): gewoon direct exporteren.
		if (!this.exportUitsplitsing()) return superMethod();

		// Zet export-modus aan zodat incremental list alle items rendert
		this.exportMode.set(true);

		// Wacht tot het totaal opnieuw is berekend via de Subject.
		// Subject emit alleen wanneer onTotalChanged expliciet wordt aangeroepen.
		return this.totalUitsplitsingForExport$.pipe(
			filter((totaal) => totaal !== undefined),
			take(1),
			// Wacht tot de volgende render, om te voorkomen dat de hoogtes van de incremental lists nog niet correct zijn.
			delayUntilNextRender(this.injector),
			switchMap(superMethod),
			finalize(() => this.exportMode.set(false))
		);
	}
}

function sortInterventielaag(colPath: (string | null)[], keys0: (string | null)[], keys1: (string | null)[]): (string | null)[] {
	if (colPath.length == 1) return sortLike(union(keys0, keys1), Object.values(Interventielaag));

	return sortBy(union(keys0, keys1));
}
